import {
  breakdownScript,
  createScriptComment,
  deleteScriptEpisode,
  downloadScriptExport,
  finalizeScript,
  getProduction,
  getScriptDocument,
  listProductionListings,
  listScriptDocumentComments,
  listScriptEpisodes,
  listScriptRevisions,
  lockScriptRevision,
  parseScript,
  patchScriptDocument,
  publishScriptRevision,
  resolveScriptComment,
  saveScript,
  type Production,
  type Script,
  type ScriptBreakdownResult,
  type ScriptComment,
  type ScriptDocument,
  type ScriptEpisodeList,
  type ScriptRevision,
  type StudioProductionListing,
} from '@/api/studio-production'
import {
  createWriterScriptComment,
  deleteWriterScriptEpisode,
  downloadWriterScriptExport,
  finalizeWriterScript,
  getWriterScriptDocument,
  listWriterScriptDocumentComments,
  listWriterScriptEpisodes,
  listWriterScriptRevisions,
  lockWriterScriptRevision,
  parseWriterScript,
  patchWriterScriptDocument,
  publishWriterScriptRevision,
  resolveWriterScriptComment,
  saveWriterScript,
  writerBasePath,
} from '@/api/studio-writer'
import { productionBasePath } from '@/features/studio/lib/production-path'
import {
  fetchProductionScriptEpisode,
  fetchWriterScriptEpisode,
  invalidateProductionScriptEpisodes,
  invalidateWriterScriptEpisodes,
  patchProductionScriptEpisodeCache,
  patchWriterScriptEpisodeCache,
} from '@/features/studio/lib/script-episode-cache'

export type ScriptEditorVariant = 'production' | 'personal'

export type ScriptEditorApi = {
  variant: ScriptEditorVariant
  scriptId: number
  productionId: string
  isPersonal: boolean
  hubPath: string
  editorBase: (scriptId: number) => string
  episodeEditorPath: (scriptId: number, episodeNo: number) => string
  getProduction: () => Promise<Production | null>
  getScriptDocument: (scriptId: number) => Promise<ScriptDocument>
  patchScriptDocument: (scriptId: number, body: Record<string, unknown>) => Promise<ScriptDocument>
  listEpisodes: (scriptId: number) => Promise<ScriptEpisodeList>
  listRevisions: (scriptId: number, episodeNo: number) => Promise<{ items: ScriptRevision[] }>
  listProductionListings: () => Promise<StudioProductionListing[]>
  fetchEpisode: (scriptId: number, episodeNo: number, locale?: string) => Promise<Script>
  patchEpisodeCache: (scriptId: number, episodeNo: number, patch: Partial<Script>, locale?: string) => void
  invalidateEpisodes: (scriptId: number) => void
  saveEpisode: (
    scriptId: number,
    episodeNo: number,
    body: {
      body?: string
      title?: string
      outline?: string
      translations?: Record<string, Record<string, string>>
    },
  ) => Promise<Script>
  deleteEpisode: (scriptId: number, episodeNo: number) => Promise<{ ok: boolean }>
  parseEpisode: (scriptId: number, episodeNo: number) => Promise<Script>
  finalizeEpisode: (scriptId: number, episodeNo: number, changeNote?: string) => Promise<Script>
  publishRevision: (scriptId: number, episodeNo: number, changeNote?: string) => Promise<Script>
  lockRevision: (scriptId: number, episodeNo: number, revisionNo: number) => Promise<Script>
  createComment: (
    scriptId: number,
    episodeNo: number,
    body: string,
    anchor?: {
      scene_no?: number
      anchor_start?: number
      anchor_end?: number
      anchor_quote?: string
    },
  ) => Promise<ScriptComment>
  resolveComment: (scriptId: number, episodeNo: number, publicId: string, resolved: boolean) => Promise<ScriptComment>
  downloadExport: (
    scriptId: number,
    episodeNo: number,
    format: 'fountain' | 'pdf' | 'fdx',
    convention: 'cn' | 'hollywood',
  ) => Promise<void>
  breakdownEpisode: (scriptId: number, episodeNo: number) => Promise<ScriptBreakdownResult>
  listDocumentComments: (
    scriptId: number,
    opts?: { limit?: number; offset?: number },
  ) => Promise<{ items: ScriptComment[]; total: number }>
}

export function createScriptEditorApi(
  variant: ScriptEditorVariant,
  productionId: string,
  scriptId: number,
): ScriptEditorApi {
  const isPersonal = variant === 'personal'

  if (isPersonal) {
    const base = writerBasePath()
    return {
      variant,
      scriptId,
      productionId: '',
      isPersonal: true,
      hubPath: base,
      editorBase: (sid) => `${base}/script/${sid}/episodes`,
      episodeEditorPath: (sid, ep) => `${base}/script/${sid}/episodes/${ep}`,
      getProduction: async () => null,
      getScriptDocument: getWriterScriptDocument,
      patchScriptDocument: patchWriterScriptDocument,
      listEpisodes: listWriterScriptEpisodes,
      listRevisions: listWriterScriptRevisions,
      listProductionListings: async () => [],
      fetchEpisode: fetchWriterScriptEpisode,
      patchEpisodeCache: patchWriterScriptEpisodeCache,
      invalidateEpisodes: (sid) => invalidateWriterScriptEpisodes(sid),
      saveEpisode: saveWriterScript,
      deleteEpisode: deleteWriterScriptEpisode,
      parseEpisode: parseWriterScript,
      finalizeEpisode: finalizeWriterScript,
      publishRevision: publishWriterScriptRevision,
      lockRevision: lockWriterScriptRevision,
      createComment: (sid, ep, text, anchor) =>
        createWriterScriptComment(sid, ep, { body: text, ...anchor }),
      resolveComment: resolveWriterScriptComment,
      downloadExport: downloadWriterScriptExport,
      breakdownEpisode: async () => ({ locked_revision: 0, scenes: [], shots_created: 0, shots_skipped: 0 }),
      listDocumentComments: listWriterScriptDocumentComments,
    }
  }

  const base = productionBasePath(productionId)
  return {
    variant,
    scriptId,
    productionId,
    isPersonal: false,
    hubPath: `${base}/script`,
    editorBase: (sid) => `${base}/script/${sid}/episodes`,
    episodeEditorPath: (sid, ep) => `${base}/script/${sid}/episodes/${ep}`,
    getProduction: () => getProduction(productionId).catch(() => null),
    getScriptDocument: (sid) => getScriptDocument(productionId, sid),
    patchScriptDocument: (sid, body) => patchScriptDocument(productionId, sid, body),
    listEpisodes: (sid) => listScriptEpisodes(productionId, sid),
    listRevisions: (sid, ep) => listScriptRevisions(productionId, sid, ep),
    listProductionListings: async () => {
      const res = await listProductionListings(productionId)
      return res.items ?? []
    },
    fetchEpisode: (sid, ep, locale) => fetchProductionScriptEpisode(productionId, sid, ep, locale),
    patchEpisodeCache: (sid, ep, patch, locale) =>
      patchProductionScriptEpisodeCache(productionId, sid, ep, patch, locale),
    invalidateEpisodes: (sid) => invalidateProductionScriptEpisodes(productionId, sid),
    saveEpisode: (sid, ep, body) => saveScript(productionId, sid, ep, body),
    deleteEpisode: (sid, ep) => deleteScriptEpisode(productionId, sid, ep),
    parseEpisode: (sid, ep) => parseScript(productionId, sid, ep),
    finalizeEpisode: (sid, ep, note) => finalizeScript(productionId, sid, ep, note),
    publishRevision: (sid, ep, note) => publishScriptRevision(productionId, sid, ep, note),
    lockRevision: (sid, ep, rev) => lockScriptRevision(productionId, sid, ep, rev),
    createComment: (sid, ep, text, anchor) =>
      createScriptComment(
        productionId,
        sid,
        ep,
        text,
        anchor
          ? {
              sceneNo: anchor.scene_no,
              anchorStart: anchor.anchor_start,
              anchorEnd: anchor.anchor_end,
              anchorQuote: anchor.anchor_quote,
            }
          : undefined,
      ),
    resolveComment: (sid, ep, publicId, resolved) =>
      resolveScriptComment(productionId, sid, ep, publicId, resolved),
    downloadExport: (sid, ep, format, convention) =>
      downloadScriptExport(productionId, sid, ep, format, convention),
    breakdownEpisode: (sid, ep) => breakdownScript(productionId, sid, ep),
    listDocumentComments: (sid, opts) => listScriptDocumentComments(productionId, sid, opts),
  }
}
